import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import ShapeOverlayMenu from '../components/Navbar/ShapeOverlayMenu'
import Footer from '../components/Footer/Footer'

const BG      = '#0a0a0a'
const PRIMARY = '#ffffff'
const ACCENT  = 'rgba(255,255,255,0.12)'
const CTA     = '#c4b5fd'

const EXPERIMENTS = [
  {
    href: '/computer-vision',
    title: 'Computer Vision',
    tag: 'Interactive · 15 chapters',
    description: 'A visual journey through how machines see — convolutions, CNNs, transformers, and beyond.',
    image: '/images/experiments/computer-vision.jpg',
  },
  {
    href: '/ai',
    title: 'How AI Thinks',
    tag: 'Interactive · LLMs',
    description: 'A visual journey inside modern language models — tokens, attention, training, and inference. No equations, no jargon, just understanding.',
    image: '/images/experiments/ai.jpg',
  },
  {
    href: '/audio',
    title: 'Audio',
    tag: 'WebAudio · 3D',
    description: 'Real-time audio visualisation in 3D. Speak into your mic and watch the waveform breathe.',
    image: '/images/experiments/audio.jpg',
  },
  {
    href: '/dsa',
    title: 'DSA',
    tag: 'Fundamentals · Visual',
    description: 'Data structures and algorithms brought to life — sorting, graphs, trees, and more.',
    image: '/images/experiments/dsa.jpg',
  },
  {
    href: '/sun',
    title: 'Sun',
    tag: 'Three.js · Wireframe',
    description: 'A wireframe sun scene built with Three.js. Pure geometry, pure light.',
    image: '/images/experiments/sun.jpg',
  },
  {
    href: '/dino',
    title: 'Dino',
    tag: 'Game',
    description: 'A browser-native dino runner. Jump your way to glory.',
    image: '/images/experiments/dino.jpg',
  },
  {
    href: '/work/indian-political-compass',
    title: 'Indian Political Compass',
    tag: 'Interactive · Politics',
    description: 'Where do you stand on the Indian political spectrum? A quiz and compass built for Indian political reality, not a Western left-right import.',
    image: '/images/work/ipc-work-img.jpg',
  },
]

const EASE = [0.16, 1, 0.3, 1]

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
}

function ExperimentSection({ exp, index }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <motion.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      style={{
        paddingTop: index === 0 ? 0 : 64,
        paddingBottom: 64,
        borderTop: index === 0 ? 'none' : `1px solid color-mix(in srgb, ${ACCENT} 50%, transparent)`,
      }}
    >
      {/* Meta row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: PRIMARY, opacity: 0.5, marginBottom: 18,
      }}>
        <span>{String(index + 1).padStart(2, '0')}</span>
        <span>·</span>
        <span>{exp.tag}</span>
      </div>

      {/* Title */}
      <Link to={exp.href} style={{ textDecoration: 'none' }}>
        <h2 style={{
          fontFamily: "'Clash Display', sans-serif",
          fontSize: 'clamp(32px, 5.5vw, 64px)',
          fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.05,
          margin: 0, color: PRIMARY,
        }}>
          {exp.title}
        </h2>
      </Link>

      {/* Description — capped width for comfortable reading */}
      <p style={{
        marginTop: 18, fontSize: 17, lineHeight: 1.75,
        color: PRIMARY, opacity: 0.7, maxWidth: '62ch',
      }}>
        {exp.description}
      </p>

      {/* Image */}
      <Link to={exp.href} style={{ textDecoration: 'none' }}>
        <div style={{
          marginTop: 36, width: '100%', aspectRatio: '16/9',
          borderRadius: 12, overflow: 'hidden', position: 'relative',
        }}>
          {exp.image ? (
            <img
              src={exp.image}
              alt={exp.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              background: `linear-gradient(135deg, color-mix(in srgb, ${ACCENT} 33%, transparent) 0%, ${PRIMARY} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 64, opacity: 0.18, color: BG, fontFamily: "'Clash Display', sans-serif", fontWeight: 700 }}>
                {exp.title.split(' ').map(w => w[0]).join('')}
              </span>
            </div>
          )}
        </div>
      </Link>

      {/* Try it link */}
      <Link
        to={exp.href}
        style={{
          marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 8,
          textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: CTA,
        }}
      >
        Try it
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M3 13L13 3M13 3H6M13 3V10" stroke={CTA} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </motion.div>
  )
}

export default function Experiments() {
  const heroRef = useRef(null)
  const heroInView = useInView(heroRef, { once: true })

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      fontFamily: "'General Sans', system-ui, sans-serif",
    }}>
      <Helmet>
        <title>Experiments — Shreeram Ganeshan</title>
        <meta name="description" content="Interactive experiments in computer vision, audio, DSA visualisation, and 3D — explorations beyond client work." />
        <meta property="og:title" content="Experiments — Shreeram Ganeshan" />
        <meta property="og:description" content="Interactive experiments in computer vision, audio, DSA visualisation, and 3D." />
        <meta property="og:url" content="https://shreeram.dev/experiments" />
      </Helmet>
      <ShapeOverlayMenu />

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '120px 32px 80px' }}>

        {/* Header */}
        <motion.div
          ref={heroRef}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          style={{ marginBottom: 96 }}
        >
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: PRIMARY, opacity: 0.55, marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 22, height: 1, background: PRIMARY, opacity: 0.4 }} />
            Experiments
          </div>
          <h1 style={{
            fontFamily: "'Clash Display', sans-serif",
            fontSize: 'clamp(36px, 6vw, 72px)',
            fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.0,
            margin: 0, color: PRIMARY,
          }}>
            Creative Lab
          </h1>
          <p style={{
            marginTop: 18, fontSize: 15, lineHeight: 1.7,
            color: PRIMARY, opacity: 0.55, maxWidth: 520,
          }}>
            Interactive experiments at the intersection of code, data, and visual design.
          </p>
        </motion.div>

        {/* Stacked full-width sections */}
        {EXPERIMENTS.map((exp, i) => (
          <ExperimentSection key={exp.href} exp={exp} index={i} />
        ))}
      </div>
      <Footer />
    </div>
  )
}
